/**
 * Mock data for Backloader (Transport Association partner jobs).
 * Realistic UK locations, companies, and pre-agreed rates.
 */

export interface BackloaderPartnerCompany {
  id: string;
  name: string;
  contactEmail: string;
  contactPhone: string;
  town: string;
  postcode: string;
  /** Pre-agreed rate per pallet (£) when posting a job to this partner */
  agreedPricePerPallet: number;
}

export interface BackloaderJob {
  id: string;
  companyId: string;
  companyName: string;
  contactEmail: string;
  contactPhone: string;
  pickupTown: string;
  pickupPostcode: string;
  pickupLat: number;
  pickupLon: number;
  deliveryTown: string;
  deliveryPostcode: string;
  deliveryAddress?: string;
  deliveryLat: number;
  deliveryLon: number;
  date: string; // YYYY-MM-DD
  pallets: number;
  weightKg: number;
  pricePerPallet: number; // pre-agreed £
  totalCost: number; // £
  jobDistanceMiles: number;
  notes?: string;
}

// 3 mock partner companies (Transport Association) with pre-agreed rates
export const MOCK_PARTNER_COMPANIES: BackloaderPartnerCompany[] = [
  {
    id: 'partner-1',
    name: 'North East Freight Ltd',
    contactEmail: 'ops@northeastfreight.co.uk',
    contactPhone: '0191 234 5678',
    town: 'Newcastle upon Tyne',
    postcode: 'NE1 4ST',
    agreedPricePerPallet: 95,
  },
  {
    id: 'partner-2',
    name: 'Tyne Valley Logistics',
    contactEmail: 'planning@tynevalleylogistics.com',
    contactPhone: '01670 456 789',
    town: 'Morpeth',
    postcode: 'NE61 1AB',
    agreedPricePerPallet: 105,
  },
  {
    id: 'partner-3',
    name: 'Durham Distribution Co',
    contactEmail: 'backload@durhamdistribution.co.uk',
    contactPhone: '0191 378 9012',
    town: 'Durham',
    postcode: 'DH1 1AA',
    agreedPricePerPallet: 100,
  },
];

// Mock jobs on offer from partners (various dates around late Feb 2026)
export const MOCK_BACKLOADER_JOBS: BackloaderJob[] = [
  {
    id: 'job-1',
    companyId: 'partner-1',
    companyName: 'North East Freight Ltd',
    contactEmail: 'ops@northeastfreight.co.uk',
    contactPhone: '0191 234 5678',
    pickupTown: 'Newcastle upon Tyne',
    pickupPostcode: 'NE6 1PL',
    pickupLat: 54.9756,
    pickupLon: -1.5679,
    deliveryTown: 'Leeds',
    deliveryPostcode: 'LS10 1AB',
    deliveryAddress: 'Leeds Industrial Park',
    deliveryLat: 53.7960,
    deliveryLon: -1.5476,
    date: '2026-02-27',
    pallets: 4,
    weightKg: 2000,
    pricePerPallet: 100,
    totalCost: 400,
    jobDistanceMiles: 92,
    notes: 'Fragile, book slot with warehouse.',
  },
  {
    id: 'job-2',
    companyId: 'partner-1',
    companyName: 'North East Freight Ltd',
    contactEmail: 'ops@northeastfreight.co.uk',
    contactPhone: '0191 234 5678',
    pickupTown: 'Gateshead',
    pickupPostcode: 'NE11 0QP',
    pickupLat: 54.9520,
    pickupLon: -1.6070,
    deliveryTown: 'Manchester',
    deliveryPostcode: 'M5 2WT',
    deliveryLat: 53.4808,
    deliveryLon: -2.2426,
    date: '2026-02-27',
    pallets: 8,
    weightKg: 3200,
    pricePerPallet: 95,
    totalCost: 760,
    jobDistanceMiles: 145,
  },
  {
    id: 'job-3',
    companyId: 'partner-2',
    companyName: 'Tyne Valley Logistics',
    contactEmail: 'planning@tynevalleylogistics.com',
    contactPhone: '01670 456 789',
    pickupTown: 'Morpeth',
    pickupPostcode: 'NE61 2YX',
    pickupLat: 55.1680,
    pickupLon: -1.6880,
    deliveryTown: 'Sheffield',
    deliveryPostcode: 'S9 1AA',
    deliveryLat: 53.3811,
    deliveryLon: -1.4701,
    date: '2026-02-27',
    pallets: 6,
    weightKg: 2400,
    pricePerPallet: 105,
    totalCost: 630,
    jobDistanceMiles: 118,
  },
  {
    id: 'job-4',
    companyId: 'partner-2',
    companyName: 'Tyne Valley Logistics',
    contactEmail: 'planning@tynevalleylogistics.com',
    contactPhone: '01670 456 789',
    pickupTown: 'Alnwick',
    pickupPostcode: 'NE66 1AB',
    pickupLat: 55.4130,
    pickupLon: -1.7050,
    deliveryTown: 'Edinburgh',
    deliveryPostcode: 'EH12 9GF',
    deliveryLat: 55.9533,
    deliveryLon: -3.1883,
    date: '2026-02-28',
    pallets: 5,
    weightKg: 1500,
    pricePerPallet: 120,
    totalCost: 600,
    jobDistanceMiles: 85,
  },
  {
    id: 'job-5',
    companyId: 'partner-3',
    companyName: 'Durham Distribution Co',
    contactEmail: 'backload@durhamdistribution.co.uk',
    contactPhone: '0191 378 9012',
    pickupTown: 'Durham',
    pickupPostcode: 'DH1 1AA',
    pickupLat: 54.7761,
    pickupLon: -1.5733,
    deliveryTown: 'Birmingham',
    deliveryPostcode: 'B6 4AA',
    deliveryLat: 52.4862,
    deliveryLon: -1.8904,
    date: '2026-02-27',
    pallets: 12,
    weightKg: 4800,
    pricePerPallet: 90,
    totalCost: 1080,
    jobDistanceMiles: 178,
  },
  {
    id: 'job-6',
    companyId: 'partner-3',
    companyName: 'Durham Distribution Co',
    contactEmail: 'backload@durhamdistribution.co.uk',
    contactPhone: '0191 378 9012',
    pickupTown: 'Peterlee',
    pickupPostcode: 'SR8 1ER',
    pickupLat: 54.7610,
    pickupLon: -1.3360,
    deliveryTown: 'Middlesbrough',
    deliveryPostcode: 'TS2 1UE',
    deliveryLat: 54.5742,
    deliveryLon: -1.2349,
    date: '2026-02-27',
    pallets: 3,
    weightKg: 900,
    pricePerPallet: 85,
    totalCost: 255,
    jobDistanceMiles: 28,
  },
  {
    id: 'job-7',
    companyId: 'partner-1',
    companyName: 'North East Freight Ltd',
    contactEmail: 'ops@northeastfreight.co.uk',
    contactPhone: '0191 234 5678',
    pickupTown: 'Sunderland',
    pickupPostcode: 'SR1 1AA',
    pickupLat: 54.9069,
    pickupLon: -1.3838,
    deliveryTown: 'York',
    deliveryPostcode: 'YO1 6GA',
    deliveryLat: 53.9600,
    deliveryLon: -1.0873,
    date: '2026-02-28',
    pallets: 7,
    weightKg: 2100,
    pricePerPallet: 98,
    totalCost: 686,
    jobDistanceMiles: 68,
  },
  {
    id: 'job-8',
    companyId: 'partner-2',
    companyName: 'Tyne Valley Logistics',
    contactEmail: 'planning@tynevalleylogistics.com',
    contactPhone: '01670 456 789',
    pickupTown: 'Newcastle upon Tyne',
    pickupPostcode: 'NE2 1AB',
    pickupLat: 54.9820,
    pickupLon: -1.6170,
    deliveryTown: 'Nottingham',
    deliveryPostcode: 'NG2 1AA',
    deliveryAddress: 'Nottingham DC',
    deliveryLat: 52.9548,
    deliveryLon: -1.1581,
    date: '2026-02-27',
    pallets: 9,
    weightKg: 2700,
    pricePerPallet: 92,
    totalCost: 828,
    jobDistanceMiles: 156,
  },
  {
    id: 'job-9',
    companyId: 'partner-3',
    companyName: 'Durham Distribution Co',
    contactEmail: 'backload@durhamdistribution.co.uk',
    contactPhone: '0191 378 9012',
    pickupTown: 'Consett',
    pickupPostcode: 'DH8 0SP',
    pickupLat: 54.8500,
    pickupLon: -1.8320,
    deliveryTown: 'Liverpool',
    deliveryPostcode: 'L1 0BG',
    deliveryLat: 53.4084,
    deliveryLon: -2.9916,
    date: '2026-02-27',
    pallets: 5,
    weightKg: 1750,
    pricePerPallet: 110,
    totalCost: 550,
    jobDistanceMiles: 112,
  },
  {
    id: 'job-10',
    companyId: 'partner-1',
    companyName: 'North East Freight Ltd',
    contactEmail: 'ops@northeastfreight.co.uk',
    contactPhone: '0191 234 5678',
    pickupTown: 'Washington',
    pickupPostcode: 'NE38 7LR',
    pickupLat: 54.9010,
    pickupLon: -1.5220,
    deliveryTown: 'Doncaster',
    deliveryPostcode: 'DN2 5LT',
    deliveryLat: 53.5229,
    deliveryLon: -1.1312,
    date: '2026-02-28',
    pallets: 4,
    weightKg: 1200,
    pricePerPallet: 88,
    totalCost: 352,
    jobDistanceMiles: 95,
  },
  {
    id: 'job-11',
    companyId: 'partner-2',
    companyName: 'Tyne Valley Logistics',
    contactEmail: 'planning@tynevalleylogistics.com',
    contactPhone: '01670 456 789',
    pickupTown: 'Hexham',
    pickupPostcode: 'NE46 1AB',
    pickupLat: 54.9710,
    pickupLon: -2.1010,
    deliveryTown: 'Carlisle',
    deliveryPostcode: 'CA1 1AA',
    deliveryLat: 54.8950,
    deliveryLon: -2.9382,
    date: '2026-02-28',
    pallets: 6,
    weightKg: 1800,
    pricePerPallet: 75,
    totalCost: 450,
    jobDistanceMiles: 42,
  },
  {
    id: 'job-12',
    companyId: 'partner-3',
    companyName: 'Durham Distribution Co',
    contactEmail: 'backload@durhamdistribution.co.uk',
    contactPhone: '0191 378 9012',
    pickupTown: 'Darlington',
    pickupPostcode: 'DL1 1AA',
    pickupLat: 54.5250,
    pickupLon: -1.5520,
    deliveryTown: 'Hull',
    deliveryPostcode: 'HU1 1AA',
    deliveryAddress: 'Hull Docks',
    deliveryLat: 53.7443,
    deliveryLon: -0.3325,
    date: '2026-02-27',
    pallets: 10,
    weightKg: 3500,
    pricePerPallet: 82,
    totalCost: 820,
    jobDistanceMiles: 78,
  },
];

// UK locations for autocomplete (subset – in real app would use API)
export const MOCK_LOCATIONS = [
  { address: 'Newcastle upon Tyne, NE1 4ST', postcode: 'NE1 4ST', town: 'Newcastle upon Tyne', lat: 54.9783, lon: -1.6178 },
  { address: 'Gateshead, NE11 0QP', postcode: 'NE11 0QP', town: 'Gateshead', lat: 54.9520, lon: -1.6070 },
  { address: 'Morpeth, NE61 1AB', postcode: 'NE61 1AB', town: 'Morpeth', lat: 55.1680, lon: -1.6880 },
  { address: 'Durham, DH1 1AA', postcode: 'DH1 1AA', town: 'Durham', lat: 54.7761, lon: -1.5733 },
  { address: 'Sunderland, SR1 1AA', postcode: 'SR1 1AA', town: 'Sunderland', lat: 54.9069, lon: -1.3838 },
  { address: 'Leeds, LS10 1AB', postcode: 'LS10 1AB', town: 'Leeds', lat: 53.7960, lon: -1.5476 },
  { address: 'Manchester, M5 2WT', postcode: 'M5 2WT', town: 'Manchester', lat: 53.4808, lon: -2.2426 },
  { address: 'Sheffield, S9 1AA', postcode: 'S9 1AA', town: 'Sheffield', lat: 53.3811, lon: -1.4701 },
  { address: 'Edinburgh, EH12 9GF', postcode: 'EH12 9GF', town: 'Edinburgh', lat: 55.9533, lon: -3.1883 },
  { address: 'Birmingham, B6 4AA', postcode: 'B6 4AA', town: 'Birmingham', lat: 52.4862, lon: -1.8904 },
  { address: 'Middlesbrough, TS2 1UE', postcode: 'TS2 1UE', town: 'Middlesbrough', lat: 54.5742, lon: -1.2349 },
  { address: 'York, YO1 6GA', postcode: 'YO1 6GA', town: 'York', lat: 53.9600, lon: -1.0873 },
  { address: 'Alnwick, NE66 1AB', postcode: 'NE66 1AB', town: 'Alnwick', lat: 55.4130, lon: -1.7050 },
  { address: 'Peterlee, SR8 1ER', postcode: 'SR8 1ER', town: 'Peterlee', lat: 54.7610, lon: -1.3360 },
  { address: 'Consett, DH8 0SP', postcode: 'DH8 0SP', town: 'Consett', lat: 54.8500, lon: -1.8320 },
  { address: 'Washington, NE38 7LR', postcode: 'NE38 7LR', town: 'Washington', lat: 54.9010, lon: -1.5220 },
  { address: 'Hexham, NE46 1AB', postcode: 'NE46 1AB', town: 'Hexham', lat: 54.9710, lon: -2.1010 },
  { address: 'Darlington, DL1 1AA', postcode: 'DL1 1AA', town: 'Darlington', lat: 54.5250, lon: -1.5520 },
  { address: 'Nottingham, NG2 1AA', postcode: 'NG2 1AA', town: 'Nottingham', lat: 52.9548, lon: -1.1581 },
  { address: 'Doncaster, DN2 5LT', postcode: 'DN2 5LT', town: 'Doncaster', lat: 53.5229, lon: -1.1312 },
  { address: 'Carlisle, CA1 1AA', postcode: 'CA1 1AA', town: 'Carlisle', lat: 54.8950, lon: -2.9382 },
  { address: 'Hull, HU1 1AA', postcode: 'HU1 1AA', town: 'Hull', lat: 53.7443, lon: -0.3325 },
  { address: 'Liverpool, L1 0BG', postcode: 'L1 0BG', town: 'Liverpool', lat: 53.4084, lon: -2.9916 },
];
